/**
 * Home landing page · adaptive entry to the SPA.
 *
 * Mounted at `/`. Replaces the legacy `<Navigate to="/devices" />`
 * redirect — we no longer want to bounce users straight into an
 * ADH-REQUIRED route, because if adh isn't running the route is
 * forced over to `/local-service` by ServiceGate and they lose the
 * toolbox / settings / local-service entry context.
 *
 * Layout (top to bottom):
 *   1. Hero — project title + slogan, single-line positioning.
 *   2. ADH status banner — green pill + link to /local-service when
 *      online, amber pill + one-click launch button when offline.
 *   3. Three primary entry cards (设备控制 / 工具广场 / APK 静态分析).
 *   4. Feature grid — six static cards highlighting what the project
 *      can do. No link targets; sidebar + the three entry cards above
 *      already steer users to the live surfaces.
 *
 * Why not redirect when adh is online? The user's previous request:
 * "首页不需要重定向，adh 可用就在对应区域展示即可". The home page is a
 * stable bookmark target; auto-redirect surprises returning users.
 *
 * Auto-launch on mount:
 *   When the page loads and adh is offline, we fire `adh://launch`
 *   after a 500 ms delay so the first paint isn't stolen by the OS
 *   protocol prompt. A ref guards against double-fire on
 *   React.StrictMode dev re-mounts; we deliberately DO NOT persist a
 *   "already attempted" flag across reloads — every fresh page load
 *   is a fresh attempt (per user spec).
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  CheckCircle2,
  ExternalLink,
  FileArchive,
  KeyRound,
  MonitorPlay,
  Package,
  Rocket,
  Server,
  ShieldCheck,
  Smartphone,
  Terminal,
  Workflow,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useRuntimeConfig } from '@/lib/runtimeConfig';

const FAST_POLL_TOTAL_MS = 30_000;
const LAUNCH_URL = 'adh://launch';
const AUTO_LAUNCH_DELAY_MS = 500;

export default function Home(): JSX.Element {
  const initialised = useRuntimeConfig((s) => s.initialised);
  const lastError = useRuntimeConfig((s) => s.lastError);
  const consecutiveFailure = useRuntimeConfig((s) => s.consecutiveFailure);
  const enterFastPoll = useRuntimeConfig((s) => s.enterFastPoll);

  // adh is "online" when we got at least one successful poll or the
  // most recent failure was an HTTP/parse error (the server responded
  // with garbage but is at least reachable, so the UI still has a
  // chance). It's "offline" only when we've seen a network-class
  // failure — that's the same signal ServiceGate uses to redirect
  // ADH-required routes to `/local-service`.
  const adhOnline =
    !initialised /* assume online until proven otherwise */ ||
    lastError?.kind !== 'network' ||
    consecutiveFailure < 1;

  // Auto-launch on mount when adh is offline. Refs guard against:
  //   (a) React.StrictMode mounting the component twice in dev,
  //   (b) the user navigating back into Home in the same SPA session
  //       while a previous launch is still propagating through poll.
  // We don't persist this to sessionStorage — a hard reload (F5) should
  // re-attempt because the user clearly expects a working surface.
  const autoLaunched = useRef(false);
  useEffect(() => {
    if (autoLaunched.current) return;
    if (!initialised) return; // wait until first poll resolves so we know online state
    if (adhOnline) return;
    autoLaunched.current = true;
    const t = setTimeout(() => {
      try {
        enterFastPoll(FAST_POLL_TOTAL_MS);
        window.location.assign(LAUNCH_URL);
      } catch {
        // Sandboxed iframe / blocked protocol — the banner button
        // remains as manual fallback.
      }
    }, AUTO_LAUNCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [initialised, adhOnline, enterFastPoll]);

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      <Hero />

      <AdhStatusBanner online={adhOnline} />

      <div>
        <h2 className="mb-1 text-base font-semibold text-ink">主入口</h2>
        <p className="mb-3 text-xs text-ink-muted">
          按你接下来要做的事选一个入口。需要 adh 在线的入口在 adh 没启动时会自动标灰。
        </p>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <EntryCard
            to="/devices"
            icon={Smartphone}
            title="设备控制"
            desc="ADB / 镜像 / 文件 / 日志 / 命令中心"
            requiresAdh
            adhOnline={adhOnline}
          />
          <EntryCard
            to="/tools"
            icon={Workflow}
            title="工具广场"
            desc="JSON / 编码 / 加密 / 二维码 …（纯客户端，永远可用）"
          />
          <EntryCard
            to="/static-analysis"
            icon={FileArchive}
            title="APK 静态分析"
            desc="拖入 APK，自动反编译 + 报告"
            requiresAdh
            adhOnline={adhOnline}
          />
        </div>
      </div>

      <FeatureGrid />
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Hero                                                               */
/* ----------------------------------------------------------------- */

function Hero(): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-gradient-to-br from-accent/10 via-bg-card to-bg-card p-5 md:p-6">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent">
        ADH · Android Device Hub
      </p>
      <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-ink md:text-3xl">
        在浏览器里控制 Android 设备
      </h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted md:text-[15px]">
        一站式的 Android 设备管控台：ADB / 屏幕镜像 / 文件传输 / 应用安装 / 静态分析 / 通用工具
        全部聚到一个 SPA。Web 主站永远在线，需要操作真机时本地起一个 adh 服务即可。
      </p>
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* Status banner                                                      */
/* ----------------------------------------------------------------- */

function AdhStatusBanner({ online }: { online: boolean }): JSX.Element {
  const enterFastPoll = useRuntimeConfig((s) => s.enterFastPoll);
  const cachedVersion = useRuntimeConfig((s) => s.config.version);
  const [launching, setLaunching] = useState(false);

  const handleLaunch = (): void => {
    setLaunching(true);
    enterFastPoll(FAST_POLL_TOTAL_MS);
    try {
      window.location.assign(LAUNCH_URL);
    } catch {
      /* sandboxed iframe — surface manual fallback via /local-service */
    }
  };

  if (online) {
    return (
      <section className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-2">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          adh 在线
        </span>
        <span className="text-xs text-ink-muted">
          v{cachedVersion} · 所有功能可用
        </span>
        <NavLink
          to="/local-service"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-2 py-1 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-ink"
        >
          <Server className="h-3 w-3" />
          管理本机服务
          <ExternalLink className="h-3 w-3" />
        </NavLink>
      </section>
    );
  }

  return (
    <section className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2">
      <XCircle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
        adh 未启动
      </span>
      <span className="text-xs text-ink-muted">
        设备控制、APK 分析等需要 adh — 工具广场不受影响。若一键启动无反应，请到「手动管理」按指引安装本机服务。
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={handleLaunch}
          disabled={launching}
          className={clsx(
            'inline-flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-[11px] font-semibold text-white shadow-card transition',
            'hover:bg-accent/90 disabled:cursor-wait disabled:opacity-60',
          )}
        >
          <Rocket className="h-3 w-3" />
          {launching ? '已发起启动…' : '一键启动'}
        </button>
        <NavLink
          to="/local-service"
          className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-2 py-1 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-ink"
        >
          <Server className="h-3 w-3" />
          安装 / 手动管理
        </NavLink>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* Entry card                                                         */
/* ----------------------------------------------------------------- */

interface EntryCardProps {
  to: string;
  icon: LucideIcon;
  title: string;
  desc: string;
  /** True if this card's route is in ADH_REQUIRED_PREFIXES. */
  requiresAdh?: boolean;
  /** Live adh status (only consulted when requiresAdh is true). */
  adhOnline?: boolean;
}

function EntryCard({
  to,
  icon: Icon,
  title,
  desc,
  requiresAdh,
  adhOnline = true,
}: EntryCardProps): JSX.Element {
  const disabled = requiresAdh === true && !adhOnline;

  const base =
    'flex h-full flex-col gap-1 rounded-lg border bg-bg-card p-4 transition';

  if (disabled) {
    return (
      <div
        className={clsx(
          base,
          'cursor-not-allowed border-dashed border-line opacity-60',
        )}
        title="需要 adh 在线 — 点上方「一键启动」"
        aria-disabled
      >
        <div className="mb-1 flex items-center gap-2">
          <Icon className="h-4 w-4 text-ink-muted" />
          <span className="text-sm font-semibold text-ink-muted">{title}</span>
          <span className="ml-auto rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-px text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">
            需 adh
          </span>
        </div>
        <p className="text-xs text-ink-dim">{desc}</p>
        <p className="mt-auto text-[11px] text-ink-dim">
          adh 在线后此卡片自动恢复可点。
        </p>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      className={clsx(
        base,
        'border-line hover:border-accent/60 hover:bg-accent-soft/30 hover:shadow-card',
      )}
    >
      <div className="mb-1 flex items-center gap-2">
        <Icon className="h-4 w-4 text-accent" />
        <span className="text-sm font-semibold text-ink">{title}</span>
        {requiresAdh ? (
          <span className="ml-auto rounded-full border border-sky-500/40 bg-sky-500/10 px-1.5 py-px text-[10px] uppercase tracking-wider text-sky-700 dark:text-sky-300">
            需 adh
          </span>
        ) : (
          <span className="ml-auto rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-px text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            纯客户端
          </span>
        )}
      </div>
      <p className="text-xs text-ink-muted">{desc}</p>
    </NavLink>
  );
}

/* ----------------------------------------------------------------- */
/* Feature grid                                                       */
/* ----------------------------------------------------------------- */

interface FeatureItem {
  icon: LucideIcon;
  title: string;
  desc: string;
}

const FEATURES: readonly FeatureItem[] = [
  {
    icon: Smartphone,
    title: '设备控制',
    desc: 'USB 即插即用，扫码 Wi-Fi 一键配对，免登录直连本机。',
  },
  {
    icon: MonitorPlay,
    title: '屏幕镜像',
    desc: 'scrcpy 浮窗 + HUD，支持比特率与分辨率档位调整。',
  },
  {
    icon: Package,
    title: '应用管理',
    desc: '安装 / 卸载 / 清数据 / 启停，导出 APK 一键完成。',
  },
  {
    icon: ShieldCheck,
    title: 'APK 静态分析',
    desc: '拖入 APK 自动反编译，识别加固方案与基本面信息。',
  },
  {
    icon: Terminal,
    title: '命令中心',
    desc: 'ADB 命令收藏 / 历史 / 模板替换，支持批量执行。',
  },
  {
    icon: KeyRound,
    title: '工具广场',
    desc: 'JSON · 编解码 · 加密哈希 · 二维码，全部纯客户端。',
  },
];

function FeatureGrid(): JSX.Element {
  return (
    <section>
      <h2 className="mb-1 text-base font-semibold text-ink">能做什么</h2>
      <p className="mb-3 text-xs text-ink-muted">
        六个方向覆盖日常 Android 调试场景 —— 选一个上手，其它的等需要时再来。
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, desc }: FeatureItem): JSX.Element {
  return (
    <div className="flex h-full flex-col gap-1 rounded-lg border border-line bg-bg-card p-3 transition hover:border-accent/40 hover:bg-accent-soft/20">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 text-accent">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-ink">{title}</span>
      </div>
      <p className="text-[11px] leading-relaxed text-ink-muted">{desc}</p>
    </div>
  );
}
